import { useEffect, useRef } from "react";
import * as THREE from "three";
import { gsap } from "gsap";

// COMPASS KEY — abstract compass rose intersecting a key.
// No VA seal, military emblem, logo, flag, or government mark.
function buildObject() {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x94de52,
    metalness: 0.55,
    roughness: 0.28,
    transparent: true,
    opacity: 0,
  });

  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.028, 12, 72), mat);
  group.add(ring);
  const innerRing = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.018, 10, 64), mat);
  group.add(innerRing);

  // compass rose spikes (abstract diamonds)
  for (let i = 0; i < 4; i++) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.55, 4), mat);
    const ang = (i * Math.PI) / 2;
    spike.position.set(Math.cos(ang) * 0.62, Math.sin(ang) * 0.62, 0.02);
    spike.rotation.z = ang - Math.PI / 2;
    group.add(spike);
  }

  // intersecting key (diagonal)
  const keyGroup = new THREE.Group();
  const bow = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.045, 10, 40), mat);
  bow.position.y = 0.65;
  keyGroup.add(bow);
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 1.15, 10), mat);
  shaft.position.y = -0.05;
  keyGroup.add(shaft);
  const tooth1 = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.07, 0.07), mat);
  tooth1.position.set(0.12, -0.58, 0);
  keyGroup.add(tooth1);
  const tooth2 = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.07, 0.07), mat);
  tooth2.position.set(0.09, -0.42, 0);
  keyGroup.add(tooth2);
  keyGroup.rotation.z = -Math.PI / 5;
  keyGroup.position.z = 0.12;
  group.add(keyGroup);

  return { group, mat };
}

export const CompassKeyHover = ({ hostRef }) => {
  const mountRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isTouch || reduced) return;

    const mount = mountRef.current;
    const host = hostRef.current;
    if (!mount || !host) return;

    let renderer, raf, active = false, built = false;
    let scene, camera, group, mat;
    const targetRot = { x: 0, y: 0 };
    const MAX_TILT = (10 * Math.PI) / 180;

    const build = () => {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: "low-power" });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      mount.appendChild(renderer.domElement);

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 10);
      camera.position.z = 3;

      scene.add(new THREE.AmbientLight(0xffffff, 0.5));
      const dir = new THREE.DirectionalLight(0xffffff, 1.4);
      dir.position.set(2, 3, 4);
      scene.add(dir);
      const dir2 = new THREE.DirectionalLight(0xffffff, 0.5);
      dir2.position.set(-3, -1, 2);
      scene.add(dir2);

      const built3d = buildObject();
      group = built3d.group;
      mat = built3d.mat;
      group.position.y = -2.4;
      scene.add(group);
      built = true;
    };

    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!built) return;
      group.rotation.x += (targetRot.x - group.rotation.x) * 0.08;
      group.rotation.y += (targetRot.y - group.rotation.y) * 0.08;
      renderer.render(scene, camera);
    };

    const onEnter = () => {
      if (!built) build();
      if (!active) {
        active = true;
        loop();
      }
      gsap.to(group.position, { y: 0, duration: 0.6, ease: "power2.out" });
      gsap.to(mat, { opacity: 0.34, duration: 0.6 });
      if (glowRef.current) gsap.to(glowRef.current, { opacity: 1, duration: 0.6 });
    };

    const onLeave = () => {
      if (!built) return;
      gsap.to(group.position, { y: -2.4, duration: 0.55, ease: "power2.in" });
      gsap.to(mat, { opacity: 0, duration: 0.5 });
      if (glowRef.current) gsap.to(glowRef.current, { opacity: 0, duration: 0.5 });
      setTimeout(() => {
        cancelAnimationFrame(raf);
        active = false;
      }, 620);
    };

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      targetRot.y = nx * MAX_TILT;
      targetRot.x = ny * MAX_TILT;
    };

    host.addEventListener("pointerenter", onEnter);
    host.addEventListener("pointerleave", onLeave);
    host.addEventListener("pointermove", onMove);

    return () => {
      host.removeEventListener("pointerenter", onEnter);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(raf);
      if (renderer) {
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      }
    };
  }, [hostRef]);

  return (
    <>
      <div
        ref={glowRef}
        aria-hidden="true"
        className="absolute inset-0 opacity-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 55%, rgba(148,222,82,0.16), transparent 62%)" }}
      />
      <div ref={mountRef} aria-hidden="true" className="absolute inset-0 pointer-events-none" />
    </>
  );
};
